import React, { useEffect, useState } from 'react';
import { Tree, Button, Collapse, Dropdown, Modal, Typography, Row, Col, Input, Tooltip } from 'antd';
import { ExclamationCircleOutlined, FileAddOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import AddServiceItemModal from './ServiceItemModal';
import AppMetadataModal from './AppMetadataModal';
import ApplicationYAMLModal from './ApplicationYAMLModal';
import { ServiceItem } from '@src/types';

type Key = React.Key;
const { confirm } = Modal;

interface ApplicationPanelProps {
    application: string;
    appDeclarationData: any
    selectedServiceItem: ServiceItem | null;
    selectedService: string | null;
    setSelectedService: Function;
    serviceItems?: ServiceItemsData;
    newApplicationsCreated: string[];
    onAddServiceItem: (serviceName: string, serviceItemName: string) => void;
    onEditAppMetadata: (appName: string, metadata: object) => void;
    onNodeSelect: (nodes: Key[], info: any) => void;
    serviceItemsWithErrors: ServiceItem[];
    onRenameApp: (previousName: string, newName: string) => void;
    onRemoveApp?: (applicationName: string) => void; // For handling app selection
    isActive?: boolean;
    onToggle?: (application: string) => void;
}


// Define the service items data type
type ServiceItemsData = {
    [serviceType: string]: ServiceItem[];
};

const { TreeNode } = Tree;
const { Text } = Typography;

const ApplicationPanel = ({ application, serviceItems, onAddServiceItem, onNodeSelect, serviceItemsWithErrors, onRemoveApp, selectedServiceItem, selectedService, setSelectedService, onEditAppMetadata, appDeclarationData, onRenameApp, newApplicationsCreated, isActive, onToggle }: ApplicationPanelProps) => {

    const [isServiceItemModalOpen, setIsServiceItemModalOpen] = useState<boolean>(false)
    const [isAppMetadataModalOpen, setIsAppMetadataModalOpen] = useState<boolean>(false)
    const [isAppYamlModalOpen, setIsAppYamlModalOpen] = useState<boolean>(false)
    const [isEditingAppTitle, setIsEditingAppTitle] = useState<boolean>(false)

    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

    useEffect(() => {
        if (selectedService)
            setExpandedKeys([selectedService])
    }, [selectedService]);

    interface Data {
        [key: string]: any;
    }
    interface Item {
        [key: string]: string | number;
    }

    interface ServiceItem {
        appName: string;
        name: string;
        serviceType: string;
        data?: any;
    }

    const handleDelete = () => {
        confirm({
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to remove ${application}?`,
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                console.log('ok')
                onRemoveApp && onRemoveApp(application)
            }
        })
    }

    const appContextMenuItems = [
        { key: '0', label: 'View App YAML', onClick: () => setIsAppYamlModalOpen(true) },
        { key: '4', label: 'Rename Application', onClick: () => setIsEditingAppTitle(true) },
        { key: '1', label: 'Edit Metadata', onClick: () => setIsAppMetadataModalOpen(true) },
        { key: '2', label: 'Remove Application', onClick: () => handleDelete() },
    ];

    const renderAppPanelTitle = (appName: string) => {
        if (serviceItemsWithErrors.some(serviceItem => serviceItem.applicationName === appName)) {
            return <Text type={"danger"}>{appName} <ExclamationCircleOutlined /></Text>
        }
        const isNewApp = newApplicationsCreated.includes(appName)
        if (isEditingAppTitle) {
            return (
                <Tooltip color={'orange'} open={!isNewApp} title="Renaming this existing app will delete and recreate all its' Service Items!" trigger={['focus']}>
                    <Input defaultValue={appName} autoFocus={true} onBlur={(e) => { setIsEditingAppTitle(false); onRenameApp(application, e.target.value) }} />
                </Tooltip>
            )
        }
        return <Text strong>{appName}</Text>
    }

    const onSelect = async (nodes: Key[], info: any) => onNodeSelect(nodes, info)
    const onExpand = (expandedKeysValue: any) => setExpandedKeys(expandedKeysValue)

    const renderServiceItemNodeTitle = (applicationName: string, item: Item) => {
        if (serviceItemsWithErrors.some(serviceItem => serviceItem.name === item.name && serviceItem.applicationName === applicationName)) {
            return <span style={{ color: 'red' }}>{item.name} <ExclamationCircleOutlined /></span>
        }
        return item.name
    }

    const toggleExpandNode = (key: any) => {
        setExpandedKeys(prev => {
            const outArr = [...prev];
            if (outArr.includes(key)) {
                return outArr.filter(e => e !== key);
            } else {
                outArr.push(key);
                return outArr;
            }
        });
    };

    const renderTreeNodes = (applicationName: string, data: Data, depth: number, maxDepth: number) => {
        if (depth >= maxDepth) {
            return null; // Limit reached, stop recursion
        }
        return Object.keys(data).map((key: string) => (
            <TreeNode expanded selectable={false}
                title={
                    <span key={key} onClick={() => toggleExpandNode(key)}>{key}</span>
                }
                key={key}>
                {Array.isArray(data[key]) ? (
                    data[key].map((item: Item) => (
                        <TreeNode expanded title={renderServiceItemNodeTitle(applicationName, item)} key={`${key}\x1F${item.name}`} />
                    ))
                ) : (
                    renderTreeNodes(applicationName, data[key], depth + 1, maxDepth)
                )}
            </TreeNode>
        ));
    };

    return (
        <Collapse
            activeKey={isActive ? [application] : []}
            onChange={() => onToggle?.(application)}
            items={[{
                key: application,
                forceRender: true,
                label: renderAppPanelTitle(application),
                extra: (
                    <div onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation()}>
                        <Dropdown menu={{ items: appContextMenuItems }} trigger={['click']}>
                            <MoreOutlined />
                        </Dropdown>
                    </div>
                ),
                children: (
                    <>
                        <Row>
                            <Col span={24}>
                                <Tree expandedKeys={expandedKeys} selectedKeys={[`${selectedServiceItem?.serviceName}\x1F${selectedServiceItem?.name}`]} onSelect={onSelect} onExpand={onExpand} showLine defaultExpandAll defaultExpandParent>
                                    {renderTreeNodes(application, appDeclarationData?.services, 0, 2)}
                                </Tree>
                            </Col>
                        </Row>
                        <Row style={{ display: "flex", justifyContent: "center", alignContent: "center", marginTop: "5px" }}>
                            <Button type='dashed' icon={<FileAddOutlined />} onClick={() => setIsServiceItemModalOpen(true)}>New Service Item</Button>
                        </Row>
                        {isServiceItemModalOpen && <AddServiceItemModal handleModalSubmit={onAddServiceItem} modalVisible={isServiceItemModalOpen} setModalVisible={setIsServiceItemModalOpen} />}
                        {isAppMetadataModalOpen && <AppMetadataModal appName={application} metadata={appDeclarationData?.metadata} handleModalSubmit={onEditAppMetadata} modalVisible={isAppMetadataModalOpen} setModalVisible={setIsAppMetadataModalOpen} />}
                        {isAppYamlModalOpen && <ApplicationYAMLModal appName={application} appDeclarationData={appDeclarationData} modalVisible={isAppYamlModalOpen} setModalVisible={setIsAppYamlModalOpen} />}
                    </>
                ),
            }]}
        />
    )
}

export default ApplicationPanel